import React from 'react';
import styled from '@emotion/styled';

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-top: 1px solid #e5e7eb;
  background: #fff;
  padding: 16px;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const PageInfo = styled.span`
  color: #6b7280;
  font-size: 14px;
`;

const PageButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PageButton = styled.button`
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: ${props => props.active ? 'none' : '1px solid #e5e7eb'};
  border-radius: 20px;
  background: ${props => props.active ? '#597EF7' : '#fff'};
  color: ${props => props.active ? '#fff' : '#4b5563'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#1d4ed8' : '#f9fafb'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #f3f4f6;
  }
`;

const PageEllipsis = styled.span`
  color: #6b7280;
  padding: 0 4px;
`;

const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange
}) => {
  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(
          <PageButton
            key={i}
            active={currentPage === i}
            onClick={() => onPageChange(i)}
          >
            {i}
          </PageButton>
        );
      }
    } else {
      buttons.push(
        <PageButton
          key={1}
          active={currentPage === 1}
          onClick={() => onPageChange(1)}
        >
          1
        </PageButton>
      );

      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (startPage > 2) {
        buttons.push(<PageEllipsis key="ellipsis1">...</PageEllipsis>);
      }

      for (let i = startPage; i <= endPage; i++) {
        buttons.push(
          <PageButton
            key={i}
            active={currentPage === i}
            onClick={() => onPageChange(i)}
          >
            {i}
          </PageButton>
        );
      }

      if (endPage < totalPages - 1) {
        buttons.push(<PageEllipsis key="ellipsis2">...</PageEllipsis>);
      }

      if (totalPages > 1) {
        buttons.push(
          <PageButton
            key={totalPages}
            active={currentPage === totalPages}
            onClick={() => onPageChange(totalPages)}
          >
            {totalPages}
          </PageButton>
        );
      }
    }

    return buttons;
  };

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(startIndex + itemsPerPage - 1, totalItems);

  return (
    <PaginationContainer>
      <PageInfo>
        Showing {startIndex} to {endIndex} of {totalItems} entries
      </PageInfo>
      <PageButtons>
        <PageButton
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </PageButton>
        {renderPaginationButtons()}
        <PageButton
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </PageButton>
      </PageButtons>
    </PaginationContainer>
  );
};

export default Pagination; 